import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { FlightFaresList } from "@/features/flights/components/flight-fares/FlightFaresList";
import { FlightFaresPageHeader } from "@/features/flights/components/flight-fares/FlightFaresPageHeader";
import { getFareAnalyticsPayload } from "@/features/flights/components/flight-fares/getFareAnalyticsPayload";
import type { FlightFare } from "@/features/flights/components/flight-fares/types";
import {
  checkIsFareDefault,
  getArrivalLegFromFare,
  useFlightFares,
} from "@/features/flights/components/flight-fares/useFlightFares";
import { useFlightFaresAccessGuard } from "@/features/flights/components/flight-fares/useFlightFaresAccessGuard";
import { useSelectedFlightOffer } from "@/features/flights/hooks/flightSearch/useSelectedFlightOffer";
import { BrandedFaresTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import { formatCurrency } from "@/utils/currency";

export const Route = createFileRoute(
  "/_authenticated/flights/_flights/return/fare",
)({
  component: RouteComponent,
});

export function RouteComponent() {
  useFlightFaresAccessGuard();

  const intl = useIntl();
  const navigate = Route.useNavigate();

  const { offer, arrivalFlight } = useSelectedFlightOffer();
  const {
    arrivalFares: fares,
    isFaresLoading,
    isFaresError,
    defaultFare,
    selectedFare,
    setSelectedFare,
  } = useFlightFares({
    onFaresLoaded: (availableFares, defaultFare) => {
      const leg = getArrivalLegFromFare(defaultFare);
      rudderAnalytics.track(
        BrandedFaresTrackingEvents.USER_VIEWED_BRANDED_FARES,
        {
          type: "return",
          departure: leg?.departure ?? "",
          arrival: leg?.arrival ?? "",
          count: availableFares.length,
        },
      );
    },
  });

  // selectedFare should always exist at this point as  we are coming from the departure fare selection where a fare has to be selected to continue
  const [tripFare, setTripFare] = useState<FlightFare>(
    selectedFare ?? defaultFare,
  );

  const handleFareSelect = (fare: FlightFare) => {
    setTripFare(fare);

    if (!checkIsFareDefault(fare)) {
      const leg = getArrivalLegFromFare(fare);
      if (leg) {
        rudderAnalytics.track(
          BrandedFaresTrackingEvents.USER_SELECTED_BRANDED_FARES,
          getFareAnalyticsPayload(fare, leg, "return"),
        );
      }
    }
  };

  const handleContinue = () => {
    setSelectedFare(tripFare);

    if (!checkIsFareDefault(tripFare)) {
      const leg = getArrivalLegFromFare(tripFare);
      if (leg) {
        rudderAnalytics.track(
          BrandedFaresTrackingEvents.USER_CONFIRMED_BRANDED_FARES,
          getFareAnalyticsPayload(tripFare, leg, "return"),
        );
      }
    }

    navigate({
      to: "/flights/offer/$id/step/1",
      params: { id: offer.index },
    });
  };

  return (
    <main className="no-footer with-drawer main h-screen px-main pt-main">
      <div className="flex flex-col gap-4">
        <FlightFaresPageHeader
          header={intl.formatMessage({
            defaultMessage: "Select return fare",
            description: "Page title when selecting the return fare",
          })}
          // Safe to assert as existence checked in useSelectedFlightOffer
          flight={arrivalFlight!}
          stepper={{ currentStep: 2, totalSteps: 2 }}
        />

        <FlightFaresList
          fares={fares}
          getFareLeg={getArrivalLegFromFare}
          selectedFare={tripFare}
          onFareSelect={handleFareSelect}
          isLoading={isFaresLoading}
          isError={isFaresError}
          baselinePriceDifference={selectedFare?.priceDifference}
        />
      </div>

      <PersistentDrawer>
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm font-medium text-text-primary">
            {intl.formatMessage({
              defaultMessage: "Total roundtrip price",
              description:
                "Label for the total price of the roundtrip flight in the fare selection step",
            })}
          </p>

          <p className="text-base font-semibold text-text-primary">
            {formatCurrency(tripFare.totalPrice, tripFare.currency)}
          </p>
        </div>

        <Button
          fullWidth
          variant="contained"
          onClick={handleContinue}
          disabled={!tripFare}
        >
          {intl.formatMessage({
            defaultMessage: "Continue",
            description:
              "Primary button to proceed to the next flight booking step",
          })}
        </Button>
      </PersistentDrawer>
    </main>
  );
}
